import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';
import './Hero.css';

const cyclingWords = [
  'Excellence',
  'Solutions',
  'Products',
  'Dominance',
  'Innovation',
];

const PAUSE_AFTER = 1800; // ms pause after fully typed
const PAUSE_BEFORE = 300;  // ms pause after fully deleted

const useTypewriter = (words) => {
  const [displayed, setDisplayed] = useState('');

  // All mutable values in refs — no stale closures
  const wordIdxRef = useRef(0);
  const charIdxRef = useRef(0);
  const deletingRef = useRef(false);
  const pauseRef = useRef(null); // null = not paused

  useEffect(() => {
    const tick = () => {
      // Still in pause? wait.
      if (pauseRef.current !== null) {
        pauseRef.current -= 50;
        if (pauseRef.current <= 0) pauseRef.current = null;
        return;
      }

      const target = words[wordIdxRef.current];

      if (!deletingRef.current) {
        // Typing
        if (charIdxRef.current < target.length) {
          charIdxRef.current += 1;
          setDisplayed(target.slice(0, charIdxRef.current));
        } else {
          // Done typing — pause then delete
          pauseRef.current = PAUSE_AFTER;
          deletingRef.current = true;
        }
      } else {
        // Deleting
        if (charIdxRef.current > 0) {
          charIdxRef.current -= 1;
          setDisplayed(target.slice(0, charIdxRef.current));
        } else {
          // Done deleting — pause then next word
          pauseRef.current = PAUSE_BEFORE;
          deletingRef.current = false;
          wordIdxRef.current = (wordIdxRef.current + 1) % words.length;
        }
      }
    };

    const id = setInterval(tick, 50);
    return () => clearInterval(id);
  }, [words]);

  return displayed;
};

const Hero = ({ onOpenForm }) => {
  const displayed = useTypewriter(cyclingWords);

  return (
    <section className="hero" id="home">
      <div className="glow-container">
        <div className="glow-circle glow-1"></div>
        <div className="glow-circle glow-2"></div>
      </div>

      <div className="hero-container">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="hero-badge">
            <span className="badge-dot"></span>
            Building the Next Generation of SaaS
          </div>

          <h1 className="hero-title">
            Transforming Ideas into
            <div className="hero-title-line2">
              <span className="text-gradient-static">Engineered&nbsp;</span>
              <span className="text-gradient typewriter-text">
                {displayed}<span className="typewriter-cursor">|</span>
              </span>
            </div>
          </h1>

          <p className="hero-subtitle">
            Amavix Tech is a product-focused software studio. We partner with ambitious startups to build scalable platforms, AI solutions, and high-performance systems.
          </p>

          <div className="hero-actions">
            <button className="btn btn-primary" onClick={onOpenForm}>
              Launch Project <ArrowRight size={18} />
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                const processSection = document.getElementById('process');
                if (processSection) {
                  processSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              Our Methodology <Play size={16} fill="currentColor" />
            </button>
          </div>

          <div className="hero-trust">
            <span className="trust-label">Powering innovative companies:</span>
            <div className="trust-logos">
              <span className="t-logo">NEXUS.AI</span>
              <span className="t-logo">QUANTUM</span>
              <span className="t-logo">ECOFLOW</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="hero-preview-container"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <div className="preview-card glass">
            <div className="preview-header">
              <div className="preview-dots">
                <span></span><span></span><span></span>
              </div>
              <div className="preview-address">amavix.cloud/analytics</div>
            </div>
            <div className="preview-body">
              <div className="p-stats-header">
                <h3>Live System Metrics</h3>
                <span className="p-status">● Operational</span>
              </div>
              <div className="preview-stats">
                <div className="stat-item">
                  <div className="stat-label">System Uptime</div>
                  <div className="stat-value">99.99%</div>
                  <div className="stat-bar"><div className="bar-fill orange" style={{ width: '90%', background: 'var(--accent-primary)' }}></div></div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">AI Accuracy</div>
                  <div className="stat-value">98.4%</div>
                  <div className="stat-bar"><div className="bar-fill orange" style={{ width: '85%', background: '#f97316' }}></div></div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Data Processed</div>
                  <div className="stat-value">2.4 TB</div>
                  <div className="stat-bar"><div className="bar-fill orange" style={{ width: '70%', background: '#ea580c' }}></div></div>
                </div>
              </div>
              <div className="p-recent-activity">
                <div className="activity-item">
                  <CheckCircle size={14} color="var(--accent-primary)" />
                  <span>API Integration successful</span>
                </div>
                <div className="activity-item">
                  <CheckCircle size={14} color="var(--accent-primary)" />
                  <span>Vector Database synced</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
